/**
 * Optimistic update orchestration for sendMessage mutation.
 *
 * Extracted from messages.ts to isolate the "what happens optimistically when
 * you send" logic. This file owns:
 *   - Optimistic temp message insertion
 *   - Conversation list preview updates
 *   - Safety timeout
 *   - Success reconciliation (temp→real replacement)
 *   - Failure handling (mark as failed, rollback)
 */
import { messagesLogger } from '@/core/utils/logger';
import { baseApi } from '../../../core/baseApi';
import { conversationQueriesApi } from '../../conversations/endpoints/queries';
import { isSessionInvalidated } from '@/api/core/baseQueryWithRetry';
import { extractErrorMessage } from '@/shared/utils/toast';
import { getErrorMessage } from '@voley/shared-utils';
import { withScope, captureException } from '@sentry/react';
import { getSentryUserContext } from '@/shared/utils/sentryContext';
import { compareMessagesByTimestamp } from '../utils/normalize';
import { trackBlobUrl, revokeAttachmentBlobUrls } from '../utils/blob-lifecycle';
import { MESSAGES_CACHE_PARAMS, SEND_SAFETY_TIMEOUT_MS } from '../contracts/messaging-contracts';
import type { Message, SendMessageData } from '../types';
import type { RootState } from '@/core/store';

// Late-bound reference to messagesApi to avoid circular import.
// Set by messages.ts at module init time.
let _messagesApi: typeof import('./messages').messagesApi | null = null;

export function setMessagesApiRef(api: typeof import('./messages').messagesApi) {
  _messagesApi = api;
}

function getMessagesApi() {
  if (!_messagesApi) throw new Error('messagesApi ref not set — call setMessagesApiRef first');
  return _messagesApi;
}

// Monotonic counter for stable message ordering when sending rapidly
let messageSequence = 0;

const buildClientMessageId = (conversationId: string) =>
  `client_${conversationId}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

/**
 * RTK Query `onQueryStarted` handler for the sendMessage mutation.
 * Called by messages.ts — this function owns the entire optimistic lifecycle.
 */
export async function sendMessageOptimistic(
  args: { data: SendMessageData },
  api: {
    dispatch: (action: unknown) => unknown;
    queryFulfilled: Promise<{ data: Message }>;
    getState: () => unknown;
  }
) {
  const messagesApi = getMessagesApi();
  const { dispatch, queryFulfilled, getState } = api;
  const { data } = args;

  if (!data.clientMessageId) {
    data.clientMessageId = buildClientMessageId(data.conversationId);
  }

  const state = getState() as RootState;
  const userSession = state.userProfile?.session;
  const currentUserId = userSession?.id || 'current-user';
  const currentUserProfile = userSession ? {
    id: userSession.id || '',
    fullName: userSession.fullName || 'User',
    firstName: userSession.fullName?.split(' ')[0] || '',
    lastName: userSession.fullName?.split(' ').slice(1).join(' ') || '',
    profileImageUrl: userSession.profileImageUrl ?? undefined,
    avatarColor: userSession.avatarColor ?? undefined,
    type: 'user' as const
  } : undefined;

  // Build optimistic attachments from whichever path is active.
  const hasPresignedFiles = data.tempFileKeys && data.tempFileKeys.length > 0;
  const optimisticAttachments = data.files && data.files.length > 0
    ? data.files.map((file, index) => {
        const blobUrl = URL.createObjectURL(file);
        trackBlobUrl(data.conversationId, blobUrl);
        return {
          id: `temp_attachment_${Date.now()}_${index}`,
          type: file.type.startsWith('image/') ? 'image' as const : 'file' as const,
          name: file.name,
          url: blobUrl,
          size: file.size,
          mimeType: file.type,
          fileType: file.type,
          fileName: file.name,
          fileSize: file.size,
          fileUrl: blobUrl,
          previewUrl: file.type.startsWith('image/') ? blobUrl : undefined
        };
      })
    : (hasPresignedFiles && data.tempFileKeys
      ? data.tempFileKeys.map((key, index) => ({
          id: `temp_attachment_${Date.now()}_${index}`,
          type: key.mimeType.startsWith('image/') ? 'image' as const : 'file' as const,
          name: key.originalName,
          url: '',
          size: key.size,
          mimeType: key.mimeType,
          fileType: key.mimeType,
          fileName: key.originalName,
          fileSize: key.size,
          fileUrl: '',
          previewUrl: undefined
        }))
      : []);

  const seq = ++messageSequence;
  const now = Date.now();
  const isExternalDelivery = data.deliveryChannel === 'sms' || data.deliveryChannel === 'mms';

  const optimisticMessage = {
    id: `temp_${now}_${seq}`,
    conversationId: data.conversationId,
    senderId: currentUserId,
    senderType: 'user' as const,
    content: data.content,
    messageType: data.messageType || (optimisticAttachments.length > 0 || hasPresignedFiles ? 'file' : 'text'),
    deliveryChannel: data.deliveryChannel || 'internal',
    timestamp: now,
    sentAt: new Date(now).toISOString(),
    status: (isExternalDelivery ? 'pending' : 'sent') as 'pending' | 'sent',
    isLogged: false,
    attachments: optimisticAttachments,
    metadata: { clientMessageId: data.clientMessageId, _seq: seq },
    sender: currentUserProfile
  };

  // Insert optimistic message into cache
  const msgCacheKey = { parentId: data.conversationId, ...MESSAGES_CACHE_PARAMS };
  const optimisticPatch = dispatch(
    messagesApi.util.updateQueryData('getMessagesForConversation', msgCacheKey,
      (draft: Message[]) => {
        // Remove any existing temp/failed messages with the same clientMessageId
        // to prevent duplicates when a retry or fallback path re-sends.
        if (data.clientMessageId) {
          for (let i = draft.length - 1; i >= 0; i--) {
            const meta = draft[i].metadata as Record<string, unknown> | null | undefined;
            if (
              meta?.clientMessageId === data.clientMessageId &&
              (draft[i].id.startsWith('temp_') || draft[i].status === 'failed')
            ) {
              draft.splice(i, 1);
            }
          }
        }
        draft.unshift(optimisticMessage);
      }
    )
  ) as { patches: unknown[] };
  if (optimisticPatch.patches.length === 0) {
    dispatch(
      messagesApi.util.upsertQueryData('getMessagesForConversation', msgCacheKey, [optimisticMessage])
    );
  }

  // Read conversation from getConversationById cache for sidebar insertion fallback
  const cachedConvResult = conversationQueriesApi.endpoints.getConversationById.select(
    data.conversationId
  )(getState() as Parameters<ReturnType<typeof conversationQueriesApi.endpoints.getConversationById.select>>[0]);
  const cachedConversation = cachedConvResult?.data;

  const conversationPatches: Array<{ undo: () => void }> = [];
  const updateConversationDraft = (draft: { items: Array<{ id: string; lastMessage?: unknown; timestamp?: unknown; isOpen?: boolean; isActive?: boolean }>; total?: number }) => {
    if (draft && draft.items) {
      const convIndex = draft.items.findIndex((c) => c.id === data.conversationId);
      if (convIndex !== -1) {
        const conversation = draft.items[convIndex];
        const now = Date.now();
        conversation.lastMessage = data.content || (optimisticAttachments.length > 0 || hasPresignedFiles ? 'Attachment:' : '');
        conversation.timestamp = now;
        conversation.isOpen = true;
        conversation.isActive = true;
        if (convIndex > 0) {
          const [updatedConversation] = draft.items.splice(convIndex, 1);
          draft.items.unshift(updatedConversation);
        }
      } else if (cachedConversation) {
        draft.items.unshift({
          ...cachedConversation,
          lastMessage: data.content || (optimisticAttachments.length > 0 || hasPresignedFiles ? 'Attachment:' : ''),
          timestamp: Date.now(),
          isOpen: true,
          isActive: true,
        } as typeof draft.items[0]);
        if (typeof draft.total === 'number') draft.total++;
      }
    }
  };

  conversationPatches.push(dispatch(
    conversationQueriesApi.util.updateQueryData('getConversationById', data.conversationId, (draft) => {
      if (draft) {
        Object.assign(draft, { isOpen: true, isActive: true });
      }
    })
  ) as { undo: () => void });

  conversationPatches.push(dispatch(
    conversationQueriesApi.util.updateQueryData('getConversations', undefined, updateConversationDraft)
  ) as { undo: () => void });

  try {
    const rtkState = getState() as RootState;
    const apiQueries = (rtkState[baseApi.reducerPath]?.queries ?? {}) as Record<string, { originalArgs?: unknown } | undefined>;
    Object.entries(apiQueries).forEach(([key, entry]) => {
      if (!key.startsWith('getInfiniteConversations-') || !entry) return;
      conversationPatches.push(dispatch(
        conversationQueriesApi.util.updateQueryData(
          'getInfiniteConversations' as 'getConversations',
          entry.originalArgs as Parameters<typeof conversationQueriesApi.endpoints.getConversations.initiate>[0],
          updateConversationDraft
        )
      ) as { undo: () => void });
    });
  } catch (cacheErr) {
    messagesLogger.debug('[messagesApi] Infinite conversation cache patch skipped', { error: getErrorMessage(cacheErr) });
  }

  // Safety timeout
  const safetyTimeout = setTimeout(() => {
    messagesLogger.warn('[messagesApi] Send safety timeout — refetching messages for reconciliation', {
      conversationId: data.conversationId,
      clientMessageId: data.clientMessageId,
    });
    dispatch(messagesApi.util.invalidateTags([
      { type: 'Messages', id: data.conversationId }
    ]));
  }, SEND_SAFETY_TIMEOUT_MS);

  try {
    const { data: result } = await queryFulfilled;
    clearTimeout(safetyTimeout);

    const hasRealAttachments = Array.isArray(result?.attachments) &&
      result.attachments.length > 0 &&
      result.attachments.some(a => a.url && !a.url.startsWith('blob:'));

    const shouldKeepOptimisticAttachments =
      optimisticAttachments.length > 0 && !hasRealAttachments;

    if (!shouldKeepOptimisticAttachments) {
      revokeAttachmentBlobUrls(optimisticAttachments);
    }

    dispatch(
      messagesApi.util.updateQueryData('getMessagesForConversation',
        { parentId: data.conversationId, ...MESSAGES_CACHE_PARAMS },
        (draft: Message[]) => {
          const optimisticIndex = draft.findIndex(msg => msg.id === optimisticMessage.id);

          if (result) {
            const attachmentsToUse = shouldKeepOptimisticAttachments
              ? optimisticAttachments
              : (result.attachments && result.attachments.length > 0 ? result.attachments : []);

            const keepPlaceholders = hasPresignedFiles &&
              optimisticAttachments.length > 0 &&
              attachmentsToUse.length === 0;
            const finalAttachments = keepPlaceholders
              ? optimisticAttachments
              : attachmentsToUse;

            const realMessage: Message = {
              ...result,
              status: result.status || (isExternalDelivery ? 'pending' : 'sent'),
              attachments: finalAttachments,
            };

            if (optimisticIndex !== -1) {
              const existingAtts = draft[optimisticIndex].attachments;
              const hasExistingRealUrls = Array.isArray(existingAtts) && existingAtts.length > 0 &&
                existingAtts.some(a => a.url && !a.url.startsWith('blob:') && a.url !== '');
              if (hasExistingRealUrls) {
                realMessage.attachments = existingAtts;
              }
              Object.assign(draft[optimisticIndex], realMessage);
            } else {
              const existingIndex = draft.findIndex(m => m.id === realMessage.id);
              if (existingIndex !== -1) {
                const existingAttachments = draft[existingIndex].attachments;
                const hasExistingRealUrls = Array.isArray(existingAttachments) && existingAttachments.length > 0 &&
                  existingAttachments.some(a => a.url && !a.url.startsWith('blob:') && a.url !== '');
                const mergedAttachments = hasExistingRealUrls
                  ? existingAttachments
                  : (attachmentsToUse.length > 0
                    ? attachmentsToUse
                    : (Array.isArray(existingAttachments) && existingAttachments.length > 0 ? existingAttachments : []));
                Object.assign(draft[existingIndex], { ...realMessage, attachments: mergedAttachments });
              } else {
                draft.push(realMessage);
              }
            }

            // Deduplicate
            const realId = realMessage.id;
            let firstSeen = false;
            for (let i = draft.length - 1; i >= 0; i--) {
              if (draft[i].id === realId) {
                if (firstSeen) {
                  draft.splice(i, 1);
                } else {
                  firstSeen = true;
                }
              }
            }

            draft.sort(compareMessagesByTimestamp);
          }
        }
      )
    );

  } catch (error: unknown) {
    clearTimeout(safetyTimeout);
    const errorObj = error as Record<string, unknown>;
    const errorDetails = {
      message: errorObj?.message || getErrorMessage(error),
      data: errorObj?.data,
      status: errorObj?.status,
    };

    messagesLogger.error('[messagesApi] Error in onQueryStarted:', errorDetails);

    withScope((scope) => {
      scope.setTag('messaging.flow', 'send_message');
      scope.setTag('messaging.channel', data.deliveryChannel || 'internal');
      const userCtx = getSentryUserContext();
      if (userCtx.userId) scope.setTag('user_id', userCtx.userId);
      if (userCtx.organizationId) scope.setTag('organization_id', userCtx.organizationId);
      scope.setExtra('clientMessageId', data.clientMessageId);
      scope.setExtra('conversationId', data.conversationId);
      scope.setFingerprint(['message-send-failure', data.deliveryChannel || 'internal', String(errorDetails.status || 'unknown')]);
      captureException(
        error instanceof Error ? error : new Error(`Message send failed: ${errorDetails.message}`)
      );
    });

    revokeAttachmentBlobUrls(optimisticAttachments);

    dispatch(
      messagesApi.util.updateQueryData('getMessagesForConversation',
        { parentId: data.conversationId, ...MESSAGES_CACHE_PARAMS },
        (draft: Message[]) => {
          let targetIndex = draft.findIndex(msg => msg.id === optimisticMessage.id);
          if (targetIndex === -1 && data.clientMessageId) {
            targetIndex = draft.findIndex(msg =>
              (msg.metadata as Record<string, unknown> | null | undefined)?.clientMessageId === data.clientMessageId
            );
          }

          if (targetIndex !== -1) {
            const reconciledMsg = draft[targetIndex];
            if (
              data.clientMessageId &&
              reconciledMsg.id !== optimisticMessage.id &&
              !reconciledMsg.id.startsWith('temp_')
            ) {
              messagesLogger.info(
                '[messagesApi] Skipping failed status — message already reconciled by socket',
                { messageId: reconciledMsg.id, clientMessageId: data.clientMessageId }
              );
              return;
            }

            const errorText = isSessionInvalidated()
              ? 'Session expired — please log in again'
              : extractErrorMessage(error, 'Failed to send message');

            Object.assign(draft[targetIndex], {
              status: 'failed' as const,
              metadata: {
                ...draft[targetIndex].metadata,
                retry: { attempts: 0 },
                error: errorText
              }
            });
          } else {
            const errorText = isSessionInvalidated()
              ? 'Session expired — please log in again'
              : extractErrorMessage(error, 'Failed to send message');

            const failedMessage = {
              ...optimisticMessage,
              status: 'failed' as const,
              metadata: {
                ...optimisticMessage.metadata,
                retry: { attempts: 0 },
                error: errorText
              }
            };
            draft.push(failedMessage);
            draft.sort(compareMessagesByTimestamp);
          }
        }
      )
    );

    conversationPatches.forEach(p => p.undo());
  }
}
